import { Box, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useSampleDataContext } from '@resistapp/client/contexts/sample-data-context';
import { QueryFilters } from '@resistapp/client/hooks/use-query-filters/use-query-filters';
import { L2Targets } from '@resistapp/common/assays';
import { FullAbundance } from '@resistapp/common/types';
import { chain } from 'lodash';
import { Fragment, useEffect, useRef, useState } from 'react';
import { theme } from '../shared/theme';

interface Props {
  data: FullAbundance[];
  title?: string;
  hideLegend?: boolean;
}

const mstColorByAssay: Partial<Record<string, string>> = {
  AY636: theme.colors.neutral500,
  AY634: theme.colors.chartOrangeMid,
  AY633: theme.colors.chartPurpleDark,
  AY635: theme.colors.red600,
};

const legacyColorByLabel: Partial<Record<string, string>> = {
  ['Universal Bacteroidales']: theme.colors.neutral500,
  ['Swine Bacteroidales']: theme.colors.red600,
  ['Bovine Bacteroidales']: theme.colors.chartOrangeMid,
  ['Ruminant Bacteroidales']: theme.colors.chartOrangeMid,
  ['Human Bacteroides']: theme.colors.chartPurpleDark,
  universal: theme.colors.neutral500,
  swine: theme.colors.red600,
  bovine: theme.colors.chartOrangeMid,
  ruminant: theme.colors.chartOrangeMid,
  human: theme.colors.chartPurpleDark,
};

export function MSTHorizontalBarGraph({ data, title, hideLegend = false }: Props) {
  const { queryFilters }: { queryFilters: QueryFilters } = useSampleDataContext();
  const [, setRefWidthStable] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  // Combine data from multiple samples over the sampling period
  const aggregatedData = chain(data)
    .groupBy(d => d.assay)
    .map((group, assay) => {
      const totalAbsolute = chain(group)
        .map(d => d.absolute || 0)
        .sum()
        .value();

      return {
        gene: group[0].gene,
        assay,
        absolute: totalAbsolute,
      };
    })
    .value() as FullAbundance[];

  const totalNumber = aggregatedData.reduce((sum, d) => sum + getAbundaceOrZero(d), 0);
  const sortedData = aggregatedData.sort((a, b) => getAbundaceOrZero(b) - getAbundaceOrZero(a));
  const refWidth = ref.current?.clientWidth;
  const percentages = refWidth ? Array(6).fill(1) : [];

  useEffect(() => {
    setRefWidthStable(ref.current?.clientWidth ?? 0);
  }, []);

  const sortedDisplayData = sortedData.map(getMstDisplay);

  return (
    <Box px={5} pt={2} width="100%">
      {!hideLegend && (
        <LegendContainer>
          {sortedDisplayData.map(({ color, label }, index) => (
            <Fragment key={sortedData[index]?.assay ?? label}>
              <MstLegendText backgroundColor={color} opacity={sortedData[index]?.absolute ? 1 : 0.4} />
              <Flex height="20px" pl={2} pr={6} alignItems="center">
                <span style={{ opacity: sortedData[index]?.absolute ? 1 : 0.5 }}>
                  {label}
                </span>
              </Flex>
            </Fragment>
          ))}
        </LegendContainer>
      )}
      <Flex flexDirection="row" width="100%">
        <Flex alignItems="center" height="60px" pr={{ base: 2, md: 6 }}>
          {title && <BarTitle>{title}</BarTitle>}
        </Flex>
        <Flex flexDirection="column" width="100%">
          <Flex ref={ref} gap="1px">
            {sortedData.map((d, i) => {
              const { assay } = d;
              const hasSelection = queryFilters.filters.selectedTargets.length === 1;
              const isThisAssaySelected =
                hasSelection && queryFilters.filters.selectedTargets.includes(assay as L2Targets);
              const opacity = !hasSelection || isThisAssaySelected ? 1 : 0.5;
              const flexGrow = getWidth(totalNumber, d);
              if (flexGrow === 0) {
                // Avoid rendering zero-width clickable elements if flexGrow is 0
                return null;
              }
              return (
                <Box key={`bar-${i}-wrapper`} flexGrow={flexGrow}>
                  <GeneBox
                    backgroundColor={getColor(d)}
                    onClick={() => {
                      queryFilters.toggleSingleTarget(assay as L2Targets);
                    }}
                    cursor="pointer"
                    opacity={opacity}
                    transition="opacity 0.2s"
                  />
                </Box>
              );
            })}
          </Flex>
          {/* Restore PercentageContainer */}
          {refWidth && (
            <PercentageContainer
              w={`calc(100% + ${refWidth / (percentages.length - 1)}px)`}
              ml={`-${calculateLeftMargin(percentages.length, refWidth)}px`}
              mt={300}
            >
              {percentages.map((_p, i) => (
                <PercentageBox key={`percentage-${i}`} width={`${(1 / percentages.length) * 100}%`}>
                  <PercentageText>{i * 20}%</PercentageText>
                  <VerticalLine />
                </PercentageBox>
              ))}
            </PercentageContainer>
          )}
        </Flex>
      </Flex>
    </Box>
  );
}

function getAbundaceOrZero(d: FullAbundance) {
  return d.absolute ?? 0;
}

function getWidth(totalNumber: number, d: FullAbundance) {
  return getAbundaceOrZero(d) / totalNumber;
}

function getColor(d: FullAbundance) {
  return getMstDisplay(d).color;
}

function getMstDisplay(d: FullAbundance) {
  const legacyColorKey = getLegacyColorKey(d.gene);
  return {
    label: getMstLabel(legacyColorKey),
    color: mstColorByAssay[d.assay] ?? legacyColorByLabel[legacyColorKey] ?? theme.colors.neutral500,
  };
}

function getLegacyColorKey(gene: string) {
  const geneParts = gene.split('-');
  return geneParts.length > 1 ? geneParts[1] : gene;
}

function getMstLabel(colorKey: string) {
  return colorKey.includes(' ') ? colorKey.split(' ')[0]?.toLowerCase() ?? colorKey : colorKey;
}

function calculateLeftMargin(percentages: number, refWidth: number) {
  return refWidth / ((percentages - 1) * 2);
}

const PercentageText = styled.div`
  color: ${theme.colors.neutral500};
  font-size: ${theme.fontSize.small};
  font-weight: ${theme.fontWeight.heavy};
  height: 20px;
  padding-top: 6px;
`;

const VerticalLine = styled.div`
  height: 84px;
  width: 1px;
  background-color: ${theme.colors.neutral300};
`;

const PercentageBox = styled(Box)`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column-reverse;
  justify-content: center;
  align-items: center;
  text-align: center;
  margin-top: -20px;
`;

const PercentageContainer = styled(Flex)`
  height: 108px;
  padding-top: 6px;
  margin-top: -56px;
  pointer-events: none;
`;

const LegendContainer = styled(Flex)`
  padding-bottom: ${theme.spacing[8]};
  align-items: center;
  color: ${theme.colors.neutral700};
  font-size: ${theme.fontSize.medium};
  font-weight: ${theme.fontWeight.bold};
`;

const GeneBox = styled(Box)`
  height: 60px;
  border-radius: ${theme.borders.radius.small};
  width: 100%;
`;

const MstLegendText = styled(Box)`
  margin-top: 2px;
  height: 10px;
  min-width: 10px;
`;

const BarTitle = styled(Flex)`
  justify-content: flex-end; // Changed from center to flex-end for right alignment
  align-items: center;
  color: ${theme.colors.neutral700};
  font-size: ${theme.fontSize.medium};
  font-weight: ${theme.fontWeight.bold};
  width: 35px;
`;
